import { useState } from "react";
import {
  pdfHref,
  useCoverLetter,
  useCoverLetters,
  useDeleteCoverLetter,
  type CoverLetterListRow,
} from "../lib/cover_letters";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import {
  TableShell,
  Table as UITable,
  THead,
  TRow,
  TCell,
} from "../components/ui/Table";
import { Skeleton, SkeletonRow } from "../components/ui/Skeleton";
import { Sheet } from "../components/ui/Sheet";
import { activateOnKey } from "../lib/keyboard";

export default function CoverLetters() {
  const list = useCoverLetters();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const rows = list.data?.cover_letters ?? [];
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  return (
    <>
      <section className="p-4 sm:p-6 lg:p-8">
        <header className="flex items-baseline justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Cover letters
            </h1>
            <p className="mt-2 text-sm text-neutral-400">
              PDFs generated from the Jobs side panel. Cached per (job, resume).
            </p>
          </div>
          <span className="text-xs text-neutral-500" aria-live="polite">
            {list.data ? (
              `${rows.length} total`
            ) : list.isLoading ? (
              <Skeleton className="inline-block h-3 w-20" />
            ) : null}
          </span>
        </header>

        <div className="mt-4">
          {list.isLoading ? (
            <TableShell>
              <UITable>
                <THead>
                  <HeaderRow />
                </THead>
                <tbody>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <SkeletonRow key={i} cols={5} />
                  ))}
                </tbody>
              </UITable>
            </TableShell>
          ) : list.error ? (
            <TableShell>
              <EmptyState
                tone="danger"
                title="Couldn't load cover letters"
                description={String(list.error)}
                action={
                  <Button variant="secondary" onClick={() => list.refetch()}>
                    Retry
                  </Button>
                }
              />
            </TableShell>
          ) : rows.length === 0 ? (
            <TableShell>
              <EmptyState
                title="No cover letters generated. Open a job on /jobs and click Generate cover letter."
                action={
                  <Button
                    variant="secondary"
                    onClick={() => {
                      window.location.href = "/jobs";
                    }}
                  >
                    Browse jobs
                  </Button>
                }
              />
            </TableShell>
          ) : (
            <TableShell>
              <UITable>
                <THead>
                  <HeaderRow />
                </THead>
                <tbody>
                  {rows.map((row) => (
                    <Row
                      key={row.id}
                      row={row}
                      selected={selectedId === row.id}
                      onClick={() => setSelectedId(row.id)}
                    />
                  ))}
                </tbody>
              </UITable>
            </TableShell>
          )}
        </div>
      </section>

      <Sheet
        open={selected !== null}
        onOpenChange={(o) => {
          if (!o) setSelectedId(null);
        }}
      >
        <Sheet.Content side="right" className="md:max-w-xl">
          {selected ? (
            <CoverLetterSidePanel
              key={selected.id}
              listRow={selected}
              onClose={() => setSelectedId(null)}
            />
          ) : null}
        </Sheet.Content>
      </Sheet>
    </>
  );
}

function HeaderRow() {
  return (
    <tr>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Company
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Role
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Generated
      </th>
      <th scope="col" className="px-4 py-3 text-left font-medium">
        Model
      </th>
      <th scope="col" className="px-4 py-3 text-right font-medium" />
    </tr>
  );
}

function Row({
  row,
  selected,
  onClick,
}: {
  row: CoverLetterListRow;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <TRow
      tabIndex={0}
      role="button"
      className={`cursor-pointer align-top hover:bg-neutral-900/60 ${
        selected ? "bg-neutral-900/80" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => activateOnKey(e, onClick)}
      aria-selected={selected}
    >
      <TCell className="text-neutral-100">
        {row.company_name ?? <span className="text-neutral-500">—</span>}
      </TCell>
      <TCell className="text-neutral-200">
        {row.job_title ?? (
          <span className="font-mono text-xs text-neutral-500">{row.url}</span>
        )}
      </TCell>
      <TCell className="whitespace-nowrap text-neutral-400">
        {new Date(row.created_at).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </TCell>
      <TCell className="font-mono text-xs text-neutral-400">
        {shortenModel(row.model)}
      </TCell>
      <TCell className="text-right text-xs text-neutral-500">
        {row.text_chars} chars
      </TCell>
    </TRow>
  );
}

function CoverLetterSidePanel({
  listRow,
  onClose,
}: {
  listRow: CoverLetterListRow;
  onClose: () => void;
}) {
  const full = useCoverLetter(listRow.id);
  const del = useDeleteCoverLetter();

  function deleteRow() {
    if (
      !confirm(
        `Delete cover letter for ${listRow.company_name ?? "this posting"}?`,
      )
    ) {
      return;
    }
    del.mutate(listRow.id, { onSuccess: () => onClose() });
  }

  const text = full.data?.cover_letter.text;

  return (
    <>
      <Sheet.Header
        eyebrow={listRow.company_name ?? "Unknown company"}
        title={
          listRow.job_title ?? (
            <span className="break-all font-mono text-sm text-neutral-300">
              {listRow.url}
            </span>
          )
        }
      />
      <Sheet.Body className="space-y-6">
        <p className="font-mono text-[10px] text-neutral-600">{listRow.id}</p>

        <dl className="grid grid-cols-1 gap-3 text-sm">
          <PanelRow
            label="Generated"
            value={new Date(listRow.created_at).toLocaleString()}
          />
          <PanelRow label="Model" value={listRow.model} mono />
          <PanelRow label="Resume key" value={listRow.resume_key} mono />
        </dl>

        <div className="grid grid-cols-3 gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.open(listRow.url, "_blank", "noopener,noreferrer")}
          >
            Open posting
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() =>
              window.open(pdfHref(listRow.id), "_blank", "noopener,noreferrer")
            }
          >
            Download PDF
          </Button>
          <Button
            variant="danger"
            size="sm"
            loading={del.isPending}
            onClick={deleteRow}
          >
            Delete
          </Button>
        </div>

        <section className="border-t border-neutral-800 pt-4">
          <h3 className="text-xs uppercase tracking-wider text-neutral-500">
            Letter text
          </h3>
          {full.isLoading ? (
            <div className="mt-3 space-y-2">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-11/12" />
              <Skeleton className="h-3 w-5/6" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ) : full.error ? (
            <p className="mt-3 text-xs text-danger-fg">{String(full.error)}</p>
          ) : text ? (
            <pre className="mt-3 max-h-[60vh] overflow-y-auto whitespace-pre-wrap rounded border border-neutral-800 bg-neutral-900/40 p-3 text-xs leading-relaxed text-neutral-200">
              {text}
            </pre>
          ) : null}
        </section>
      </Sheet.Body>
    </>
  );
}

function PanelRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-3">
      <dt className="text-xs uppercase tracking-wider text-neutral-500">{label}</dt>
      <dd
        className={`break-all text-neutral-200 ${mono ? "font-mono text-xs" : ""}`}
      >
        {value}
      </dd>
    </div>
  );
}

function shortenModel(m: string): string {
  const slash = m.lastIndexOf("/");
  return slash >= 0 ? m.slice(slash + 1) : m;
}
